import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '~/components/ui/button';
import { api } from '~/lib/api-client';
import { proBuilderKeys } from '~/lib/query-keys';
import type { ProBuilderBoundApp, ProBuilderProject } from '~/hooks/queries/use-pro-builder';
import { useProBuilderPublishStore, type PublishRowState } from './pro-builder-publish-store';
import { useProBuilderSessionStore } from './pro-builder-session-store';
import { applyGitResult, gitProjectPath, type GitActionResult } from './pro-builder-git-actions';
import { invalidateAfterPublish, publishNotifier } from './pro-builder-publish-dialog';
import { undoAnnouncedPublish } from '~/features/apps/publish-undo';
import type { GitNeedsAction } from './publish-stream';

interface RecoveryCopy {
  id: string;
  path: string;
  createdAt: string;
  status: string;
  canDelete: boolean;
  message?: string;
}

export function ProBuilderGitPanel({ project, state, onPrepare }: {
  project: ProBuilderProject;
  state: PublishRowState | undefined;
  onPrepare: () => void;
}) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [confirmed, setConfirmed] = useState<string | null>(null);
  const running = useProBuilderPublishStore((s) => s.running.includes(project.name));
  const busy = pending || running;
  const prepared = state?.phase === 'prepared' && state.prepared.name === project.name ? state.prepared : undefined;
  const action = state?.phase === 'needs-action' ? state : state?.phase === 'prepared' ? state.action : undefined;
  const base = gitProjectPath(project.name);
  const recoveryKey = ['pro-builder-git-recovery', project.name];
  const recovery = useQuery({ queryKey: recoveryKey, queryFn: () => api.get<{ copies: RecoveryCopy[] }>(`${base}/recovery`) });
  const isBound = () => {
    const current = qc.getQueryData<ProBuilderBoundApp>(proBuilderKeys.app());
    return current?.folder === project.name && (!project.git?.workspaceId || current.project?.git?.workspaceId === project.git.workspaceId);
  };
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: proBuilderKeys.app() });
    void qc.invalidateQueries({ queryKey: proBuilderKeys.projects() });
    void qc.invalidateQueries({ queryKey: proBuilderKeys.session() });
    void qc.invalidateQueries({ queryKey: recoveryKey });
  };
  /** FRONT-3368: undoes the publish this review made, from its toast or its "Undo last publish". */
  const undoLastPublish = async (slug: string, publishId: number) => {
    const store = useProBuilderPublishStore.getState();
    if (store.running.includes(project.name)) return;
    setPending(true);
    store.setRunning(project.name, true);
    try {
      const undone = await undoAnnouncedPublish(qc, slug, publishId);
      invalidateAfterPublish(qc, slug);
      const row = useProBuilderPublishStore.getState().rows[project.name];
      if (undone && row?.phase === 'done' && row.undo?.publishId === publishId) store.setRow(project.name, row.warnings.length ? { phase: 'done', warnings: row.warnings } : null);
    } finally {
      store.setRunning(project.name, false);
      setPending(false);
    }
  };
  const run = async (path: string, body: unknown = {}) => {
    if (!isBound() || useProBuilderPublishStore.getState().running.includes(project.name)) return;
    const store = useProBuilderPublishStore.getState();
    setPending(true);
    store.setRunning(project.name, true);
    setConfirmed(null);
    try {
      const result = await api.post<GitActionResult>(`${base}/${path}`, body);
      applyGitResult(result, {
        isBound,
        setRow: (row) => store.setRow(project.name, row),
        prepared,
        invalidate,
        restart: () => useProBuilderSessionStore.getState().dispatch({ type: 'restart' }),
        published: (published) => {
          publishNotifier(project.name, toast, store.openDetails, (slug, publishId) => void undoLastPublish(slug, publishId)).published(published);
          invalidateAfterPublish(qc, published.app.slug);
        },
      });
    } catch (error) {
      if (isBound()) store.setRow(project.name, { phase: 'error', message: error instanceof Error ? error.message : 'Could not complete this action. Try again.' });
    } finally {
      store.setRunning(project.name, false);
      setPending(false);
    }
  };
  const remove = async (copy: RecoveryCopy) => {
    if (busy || useProBuilderPublishStore.getState().running.includes(project.name) || !isBound() || !copy.canDelete || !window.confirm(`Delete this local recovery copy?\n${copy.path}\nCreated ${copy.createdAt}\nThis removes the local copy permanently.`)) return;
    setPending(true);
    useProBuilderPublishStore.getState().setRunning(project.name, true);
    try {
      const result = await api.delete<{ status: 'deleted' | 'refused'; message: string }>(`${base}/recovery/${encodeURIComponent(copy.id)}`);
      if (isBound()) useProBuilderPublishStore.getState().setRow(project.name, { phase: 'ready', message: result.message });
      invalidate();
    } catch (error) {
      if (isBound()) useProBuilderPublishStore.getState().setRow(project.name, { phase: 'error', message: error instanceof Error ? error.message : 'Could not delete this copy.' });
    } finally {
      useProBuilderPublishStore.getState().setRunning(project.name, false);
      setPending(false);
    }
  };
  const canPublish = prepared?.owner === true && project.git?.owner !== false;
  const undoTarget = state?.phase === 'done' ? state.undo : undefined;
  // One decision for whether "Undo last publish" can be pressed and why not.
  const undoBlocked = busy ? 'Wait for the current step to finish.' : null;
  return (
    <div data-testid="pro-builder-git-review" className="flex flex-col gap-4 text-sm">
      {project.git?.needsMigration && <p>Your local work is preserved while this app joins its shared draft.</p>}
      {prepared && <>
        <p>Ready for review · {prepared.app.title}</p>
        <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
          <dt>Built commit</dt><dd className="break-all font-mono">{prepared.tip}</dd>
          <dt>Main</dt><dd className="break-all font-mono">{prepared.main}</dd>
          <dt>Currently live</dt><dd className="break-all font-mono">{prepared.live ?? 'Not published'}</dd>
          <dt>Audience</dt><dd>{prepared.audience}</dd>
        </dl>
        <div><p className="font-medium">Changes to publish</p><ul className="max-h-36 overflow-auto">{prepared.files.map((path) => <li className="break-all font-mono text-xs" key={path}>{path}</li>)}</ul></div>
        {prepared.warnings.map((warning, i) => <p key={i} className="text-xs text-muted-foreground">{warning}</p>)}
      </>}
      {state?.phase === 'saved' && <div role="status"><p>{state.message}</p><p className="break-all font-mono text-xs">Saved checkpoint: {state.commit}</p>{state.localChanges && <p>More local changes since this save.</p>}{!!state.notSaved?.length && <p data-testid="pro-builder-git-not-saved">Not saved to app history: {state.notSaved.join(', ')}. Links, special files and .env templates stay only in your local folder.</p>}</div>}
      {(state?.phase === 'requested' || state?.phase === 'ready') && <p role="status">{state.message}</p>}
      {state?.phase === 'publishing' && <p role="status">{state.message}</p>}
      {state?.phase === 'error' && <div role="alert"><p>{state.message}</p>{state.detail && <pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs">{state.detail}</pre>}</div>}
      {state?.phase === 'done' && state.warnings.length > 0 && <p>{state.warnings.join(' ')}</p>}
      {action && <GitActionDetails action={action} />}
      {prepared && action?.confirmation && canPublish && <label className="flex items-start gap-2"><input type="checkbox" checked={confirmed === action.confirmation} onChange={(event) => setConfirmed(event.target.checked ? action.confirmation! : null)} />I reviewed these findings and want to publish this exact commit.</label>}
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" disabled={busy || project.blockedReason !== null} onClick={() => run('save')}>Save draft</Button>
        {prepared ? <Button type="button" size="sm" disabled={busy || !!action?.confirmation && confirmed !== action.confirmation} onClick={() => run(`operations/${encodeURIComponent(prepared.operationId)}/${canPublish ? 'publish' : 'review'}`, action?.confirmation ? { confirmation: action.confirmation } : {})}>{canPublish ? action?.confirmation ? 'Publish with findings' : 'Publish' : 'Send to owner for review'}</Button> : <Button type="button" size="sm" disabled={busy || project.blockedReason !== null} onClick={onPrepare}>Build and review</Button>}
        {undoTarget && <Button type="button" size="sm" variant="outline" data-testid="pro-builder-undo-publish" disabled={undoBlocked !== null} tooltip={undoBlocked ?? 'Put back the version that was live before this publish'} onClick={() => void undoLastPublish(undoTarget.slug, undoTarget.publishId)}>Undo last publish</Button>}
        {(project.git?.updatesAvailable || action?.actions?.includes('update')) && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => run('update')}>Update from current app</Button>}
        {action?.actions?.includes('retry') && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={onPrepare}>Retry</Button>}
        {action?.actions?.includes('reconcile') ? <>
          {action.code !== 'purged' && <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => run('reconcile', { operationId: action.operationId, choice: 'local' })}>Reconcile using my local versions</Button>}
          <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => run('reconcile', { operationId: action.operationId, choice: 'current' })}>Keep current app versions</Button>
        </> : null}
      </div>
      {project.blockedReason && <p>{project.blockedReason}</p>}
      <details><summary className="cursor-pointer font-medium">Local recovery copies</summary>
        {recovery.isPending && <p>Loading local copies…</p>}
        {recovery.isError && <p role="alert">Could not load local copies. <button type="button" onClick={() => void recovery.refetch()}>Retry</button></p>}
        {recovery.data?.copies.length === 0 && <p>No local recovery copies.</p>}
        <ul className="flex max-h-64 flex-col gap-3 overflow-auto">{recovery.data?.copies.map((copy) => <li key={copy.id} className="flex flex-col gap-1 py-2">
          <p className="break-all font-mono text-xs">{copy.path}</p><p className="text-xs">{copy.createdAt} · {copy.status}</p>{copy.message && <p className="text-xs">{copy.message}</p>}
          <div className="flex flex-wrap gap-2"><Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => run(`recovery/${encodeURIComponent(copy.id)}/import`)}>Review/import</Button><Button type="button" size="sm" variant="outline" disabled={busy || !copy.canDelete} onClick={() => remove(copy)}>Delete local copy</Button></div>
        </li>)}</ul>
      </details>
    </div>
  );
}

function GitActionDetails({ action }: { action: GitNeedsAction }) {
  return <div role="alert" className="flex flex-col gap-2">
    <p>{action.message}</p>
    <ul>{action.paths.map((path) => <li key={path} className="break-all font-mono text-xs">{path}</li>)}</ul>
    {action.versions?.map((version) => <details key={version.path}><summary className="break-all">Review versions: {version.path}</summary>
      {version.binary ? <p>Binary file — content is not shown.</p> : <div className="grid grid-cols-2 gap-2">{(['local', 'current'] as const).map((kind) => <div key={kind}><p>{kind === 'local' ? 'My local version' : 'Current app version'}</p><pre className="max-h-48 overflow-auto whitespace-pre-wrap text-xs">{version[kind] === null ? 'Missing or deleted' : version[kind]}</pre></div>)}</div>}
      {version.truncated && <p>Preview truncated. Review the full versions in the terminal before choosing.</p>}
    </details>)}
  </div>;
}
